import { forwardRef, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import LinkWrap from "../../../../components/LinkWrap/component";
import Button from "../../../../components/Button/component";
import IconChevron from "../../../Icon/IconChevron";
import styles from "./styles.module.scss";
import { makeAbsoluteCBCDomain } from "../../../../utils/path/library";
import { MAXIMUM_ROWS_TO_DISPLAY } from "./constants";
import { formatRow } from "../../../../utils/schedule/format";
import { useDispatch } from "react-redux";
import { setA11yNotificationText } from "../../../../reducers/A11y/reducer";

const LoadMoreIcon = <IconChevron isDecorative={true} />;

const LoadMore = ({ onClick }) => (
    <Button
        label="Show All Events"
        className={"loadMore"}
        onClick={onClick}
        disabled={false}
        icon={LoadMoreIcon}
    />
);

const ScheduleTable = ({
    tableData = [],
    shouldLink,
    showNb,
    caption,
    zone,
}) => {
    const dispatch = useDispatch();
    const [showingAll, setShowingAll] = useState(false);
    const displayLoadMore =
        tableData.length > MAXIMUM_ROWS_TO_DISPLAY && !showingAll;
    const focusIndex = !displayLoadMore ? MAXIMUM_ROWS_TO_DISPLAY : -1;

    const loadMoreOnClick = () => {
        setShowingAll(true);
        dispatch(setA11yNotificationText("Showing All Events"));
    };

    return (
        <div
            className={styles.olympicsBroadcastSchedule}
            data-testid="olympicsBroadcastSchedule"
            data-cy="olympicsBroadcastSchedule"
        >
            <table>
                <caption className="a11y">{caption}</caption>
                <tbody>
                    <tr className="a11y">
                        <th scope="col">date</th>
                        <th scope="col">time</th>
                        <th scope="col">event</th>
                    </tr>
                    {renderRows(
                        tableData,
                        shouldLink,
                        showNb,
                        displayLoadMore,
                        focusIndex,
                        zone
                    )}
                </tbody>
            </table>
            {displayLoadMore && <LoadMore onClick={loadMoreOnClick} />}
        </div>
    );
};

ScheduleTable.propTypes = {
    tableData: PropTypes.arrayOf(
        PropTypes.shape({
            status: PropTypes.string,
            startTime: PropTypes.string,
            title: PropTypes.string,
            url: PropTypes.string,
        })
    ),
};

const mapDisplayStatus = (last) => (row) => {
    row.displayStatus = row.status;
    if (row.hideRow) {
        return row;
    }
    if (last && row.status === last.status) {
        row.a11y = true;
    }
    row.scope = "row";
    last = row;
    return row;
};

const mapHideRow = (showLoadMore) => (row, index) => {
    row.hideRow = showLoadMore && index >= MAXIMUM_ROWS_TO_DISPLAY;
    return row;
};

const renderRows = (
    tableData,
    shouldLink,
    showNb,
    showLoadMore,
    focusIndex,
    zone
) => {
    return tableData
        .map(formatRow(zone))
        .map(mapHideRow(showLoadMore))
        .map(mapDisplayStatus())
        .map((rowData, index) => {
            // FocusRow use for next item displayed after Load More button pressed
            const props = {
                shouldLink,
                showNb,
                key: `${rowData.uuid}-${index}`,
                ...rowData,
            };
            return focusIndex === index ? (
                <RowWithFocus {...props} />
            ) : (
                <Row {...props} />
            );
        });
};

const RowWithFocus = (props) => {
    const ref = useRef();
    useEffect(() => {
        ref.current.focus();
    }, []);
    return <Row ref={ref} tabIndex={-1} {...props} />;
};

const Row = forwardRef((props, ref) => {
    const hideRowClass = classNames({
        [styles.hideRow]: props.hideRow,
    });

    const tableHeader = (
        <th
            className={classNames(styles.status, { a11y: props.a11y })}
            scope={props.scope}
            data-cy="status"
        >
            {props.displayStatus}
        </th>
    );

    if (props.status === "On now" && props.shouldLink) {
        const url = makeAbsoluteCBCDomain(props.url);
        return (
            <tr className={hideRowClass} ref={ref} tabIndex={props.tabIndex}>
                {tableHeader}
                <td className={styles.live} data-cy="live">
                    <span className={styles.yellowBox}>LIVE</span>
                </td>
                <td className={styles.title} data-cy="title">
                    <span className={styles["title-text"]} data-cy="title-text">
                        {props.title}
                    </span>
                    <LinkWrap
                        className={styles["watch-link"]}
                        data-cy="watch-link"
                        external={true}
                        url={url}
                    >
                        <span className={styles["watch-background"]}>
                            <span className={styles["watch-text"]}>watch</span>
                            <span className="a11y">{props.title}</span>
                            <svg
                                width="12"
                                height="12"
                                role="presentation"
                                focusable="false"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="6" cy="6" r="6" fill="#222222" />
                                <path
                                    d="M4.33105 3.93057C4.33105 3.72912 4.54653 3.60099 4.72352 3.69719L8.61748 5.8135C8.80432 5.91505 8.80203 6.18405 8.61348 6.2824L4.71953 8.31352C4.54268 8.40576 4.33105 8.27746 4.33105 8.078V3.93057Z"
                                    fill="white"
                                />
                            </svg>
                        </span>
                    </LinkWrap>
                </td>
            </tr>
        );
    }

    const shouldHideStartTime = props.status === "On now" && !props.shouldLink;
    let nb = null;
    let titleClassName = styles.title;
    if (props.showNb && props.nb) {
        titleClassName = `${styles.title} ${styles.nb}`;
        nb = (
            <div className={styles.nbField} data-testid="nbField">
                {props.nb}
            </div>
        );
    }
    return (
        <tr className={hideRowClass} ref={ref} tabIndex={props.tabIndex}>
            {tableHeader}
            <td
                className={styles.startTime}
                data-cy="startTime"
                suppressHydrationWarning={true}
            >
                <div className={styles.statusCompressed}>{props.status}</div>
                {shouldHideStartTime ? "" : props.startTime}
            </td>
            <td className={titleClassName} data-cy="title">
                <div>{props.title}</div>
                {nb}
            </td>
        </tr>
    );
});

export default ScheduleTable;
